import { useKestrel } from 'features/kestrelViewer/KestrelProvider'
import { useEffect, useRef, useState } from 'react'
import { KVAnnotation } from 'types'

export const TAG_KEY_DISPLAY_OVERWRITES: Record<string, string> = {
  inspectionArea: 'Inspection Area',
}

export const getTagDisplayName = (databaseKey: string): string => {
  return TAG_KEY_DISPLAY_OVERWRITES[databaseKey] || databaseKey
}

export const getTagDatabaseKey = (displayName: string): string => {
  const entry = Object.entries(TAG_KEY_DISPLAY_OVERWRITES).find(
    ([, display]) => display === displayName
  )
  return entry ? entry[0] : displayName
}

export const useTagOptions = () => {
  const { annotations, inspectionAreas } = useKestrel()

  const [tagOptions, setTagOptions] = useState<Record<string, Set<string>>>({})
  const [derivedTags, setDerivedTags] = useState<string[]>([])
  const [valueOverrides, setValueOverrides] = useState<Record<string, string>>(
    {}
  )

  // Track annotations by ID and reference for efficient change detection
  const prevAnnotationsMapRef = useRef<Map<string, KVAnnotation>>(new Map())

  useEffect(() => {
    if (!annotations) {
      setTagOptions({})
      setDerivedTags([])
      prevAnnotationsMapRef.current.clear()
      return
    }

    const prevMap = prevAnnotationsMapRef.current
    const currentMap = new Map<string, KVAnnotation>()
    const addedOrChanged: Array<{
      annotation: KVAnnotation
      prev?: KVAnnotation
    }> = []
    const currentIds = new Set<string>()

    // Single pass: build current map and detect changes simultaneously
    for (const annotation of annotations) {
      currentIds.add(annotation.id)
      currentMap.set(annotation.id, annotation)

      const prev = prevMap.get(annotation.id)
      // Check if new or if tags reference changed (object identity check)
      if (!prev || prev.tags !== annotation.tags) {
        addedOrChanged.push({ annotation, prev })
      }
    }

    // Find removed annotations (only iterate over previous map, not current annotations)
    const removedIds: string[] = []
    for (const [id] of prevMap) {
      if (!currentIds.has(id)) {
        removedIds.push(id)
      }
    }

    // Only update state if there are actual changes
    if (addedOrChanged.length === 0 && removedIds.length === 0) {
      prevAnnotationsMapRef.current = currentMap
      return
    }

    // Apply incremental updates
    setTagOptions((prevOptions) => {
      const newOptions = { ...prevOptions }

      // Deep clone the Sets for immutability
      for (const key in newOptions) {
        newOptions[key] = new Set(newOptions[key])
      }

      // Remove old values from changed annotations
      for (const { prev } of addedOrChanged) {
        if (prev?.tags) {
          for (const [tag, metadata] of Object.entries(prev.tags)) {
            if (metadata.value && metadata.value.trim() !== '') {
              newOptions[tag]?.delete(metadata.value)
              if (newOptions[tag]?.size === 0) {
                delete newOptions[tag]
              }
            }
          }
        }
      }

      // Remove values from deleted annotations
      for (const id of removedIds) {
        const removed = prevMap.get(id)
        if (removed?.tags) {
          for (const [tag, metadata] of Object.entries(removed.tags)) {
            if (metadata.value && metadata.value.trim() !== '') {
              newOptions[tag]?.delete(metadata.value)
              if (newOptions[tag]?.size === 0) {
                delete newOptions[tag]
              }
            }
          }
        }
      }

      // Add new values from changed annotations
      for (const { annotation } of addedOrChanged) {
        if (annotation.tags) {
          for (const [tag, metadata] of Object.entries(annotation.tags)) {
            if (!newOptions[tag]) {
              newOptions[tag] = new Set<string>()
            }
            if (metadata.value && metadata.value.trim() !== '') {
              newOptions[tag].add(metadata.value)
            }
          }
        }
      }

      return newOptions
    })

    // Update derived tags (only if there were changes)
    setDerivedTags(() => {
      const derivedSet = new Set<string>()
      for (const annotation of currentMap.values()) {
        if (annotation.tags) {
          for (const [tag, metadata] of Object.entries(annotation.tags)) {
            if (metadata.derivedTag) {
              derivedSet.add(tag)
            }
          }
        }
      }
      return Array.from(derivedSet)
    })

    prevAnnotationsMapRef.current = currentMap
  }, [annotations])

  useEffect(() => {
    if (!inspectionAreas) return

    const localValueOverrides: Record<string, string> = {}
    for (const inspectionArea of inspectionAreas) {
      localValueOverrides[inspectionArea.id] = inspectionArea.label
    }

    setValueOverrides(localValueOverrides)
  }, [inspectionAreas])

  return {
    tagOptions,
    valueOverrides,
    derivedTags,
  }
}
